import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import VoiceRecorder from "../components/VoiceRecorder";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Save, Sparkles, Share2, ArrowLeft, Loader2, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

import { useAutosaveInterview } from "../hooks/useAutosaveInterview";
import ClientTab from "../components/interview/ClientTab";
import ContextTab from "../components/interview/ContextTab";
import PainsTab from "../components/interview/PainsTab";
import ToolsTab from "../components/interview/ToolsTab";
import GoalsTab from "../components/interview/GoalsTab";
import ReadinessTab from "../components/interview/ReadinessTab";
import FinanceTab from "../components/interview/FinanceTab";
import TranscriptTab from "../components/interview/TranscriptTab";
import ReviewProfileTab from "../components/interview/ReviewProfileTab";
import ServiceRecommendationCard from "../components/ServiceRecommendationCard";

const BASE_TABS = [
  ["client", "Client"],
  ["context", "Context"],
  ["pains", "Pain Points"],
  ["tools", "Tool Stack"],
  ["goals", "Goals"],
  ["readiness", "Readiness"],
  ["finance", "Financial"],
];
const CONSULTANT_TABS = [...BASE_TABS, ["transcript", "Transcript"], ["review", "Review Profile"]];

export default function InterviewPage({ publicMode = false }) {
  const { id, token } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("client");
  const [analyzing, setAnalyzing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const url = publicMode ? `/public/interviews/${token}` : `/interviews/${id}`;
  const { data, setData, update, flushSave, saving } = useAutosaveInterview(url);

  const isAiAssessmentIntake = (data?.intake_type || "full_audit") === "ai_assessment";
  const showTranscriptTab = !publicMode && !isAiAssessmentIntake;
  const showServiceRecommendation = !publicMode && !isAiAssessmentIntake;
  const tabsToShow = publicMode
    ? BASE_TABS
    : showTranscriptTab
      ? CONSULTANT_TABS
      : BASE_TABS;

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      await api.post(`/interviews/${id}/analyze`);
      // Background-task pattern: poll status until ready/failed (max ~3 min)
      let done = false;
      const MAX_POLLS = 90;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        try {
          const s = await api.get(`/interviews/${id}/analyze/status`);
          if (s.data.status === "ready") {
            done = true;
            break;
          }
          if (s.data.status === "failed") {
            throw new Error(s.data.error || "Analysis failed");
          }
        } catch (pollErr) {
          // network blip — keep polling
          if (pollErr instanceof Error && pollErr.message && pollErr.message !== "Analysis failed") {
            // pass through; the for-loop will retry
          } else if (pollErr?.message === "Analysis failed") {
            throw pollErr;
          }
        }
      }
      if (!done) throw new Error("Analysis timed out — please try again.");
      toast.success("Assessment generated");
      nav(`/interviews/${id}/assessment`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  const share = async () => {
    const r = await api.post(`/interviews/${id}/share`);
    const link = `${window.location.origin}/public/${r.data.share_token}`;
    await navigator.clipboard.writeText(link);
    toast.success("Share link copied to clipboard");
    setData((prev) => ({ ...(prev || {}), share_token: r.data.share_token, status: "shared" }));
  };

  const submitPublic = async () => {
    if (!window.confirm("Submit your interview to Goldline Advisory? You won't be able to edit it after.")) return;
    setSubmitting(true);
    try {
      await flushSave();
      await api.post(`/public/interviews/${token}/submit`);
      setData((prev) => ({ ...prev, status: "submitted", submitted_at: new Date().toISOString() }));
      toast.success("Interview submitted — Goldline has been notified.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed — please try again.");
    } finally { setSubmitting(false); }
  };

  const voiceUpload = async (blob, section = null) => {
    const fd = new FormData();
    fd.append("file", blob, "interview.webm");
    if (section) fd.append("section", section);
    const voiceUrl = publicMode ? `/public/interviews/${token}/voice-fill` : `/interviews/${id}/voice-fill`;
    try {
      const r = await api.post(voiceUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData(r.data.updated);
      toast.success(section
        ? `"${section.replace("_", " ")}" filled from your recording.`
        : "Form filled from your recording — review and edit as needed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Voice fill failed");
    }
  };

  if (!data) return <Layout><div className="max-w-5xl mx-auto p-12 text-navy/50">Loading...</div></Layout>;

  // Public-link thank-you screen — replaces the form once the client has submitted.
  if (publicMode && data.status === "submitted") {
    return (
      <div className="min-h-screen bg-ivory grain flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl w-full bg-white border-t-4 border-gold p-10 md:p-14 text-center">
          <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-4" strokeWidth={1.5} />
          <div className="eyebrow text-gold">Submitted</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy mt-3">Thank you{data.client_name ? `, ${data.client_name.split(" ")[0]}` : ""}.</h1>
          <div className="w-12 h-[2px] bg-gold mx-auto my-6" />
          <p className="text-navy/70 leading-relaxed">
            Your AI Tools Assessment intake is in. Goldline Advisory has been notified
            and will be in touch shortly with your custom recommendations.
          </p>
          <p className="text-xs text-navy/40 mt-8">
            Submitted {data.submitted_at ? new Date(data.submitted_at).toLocaleString() : "just now"}
          </p>
        </div>
      </div>
    );
  }

  const progress = computeProgress(data);

  const content = (
    <div className="max-w-6xl mx-auto px-6 py-10 space-y-6">
      {!publicMode && (
        <Link to="/dashboard" className="inline-flex items-center text-sm text-navy/60 hover:text-gold mb-2" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
        </Link>
      )}

      {/* Header */}
      <div className="bg-white border border-navy/10 p-8 md:p-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="eyebrow text-gold">
              {isAiAssessmentIntake ? "AI Assessment Intake" : "Client Interview · Full Audit"}
            </div>
            <h1 className="font-display text-4xl text-navy mt-2">{data.business_name || data.client_name}</h1>
            <p className="text-sm text-navy/50 mt-2">{data.client_name} · {data.industry || "Industry TBD"}</p>
          </div>
          {!publicMode && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={share} data-testid="share-btn" className="rounded-none border-navy/20">
                <Share2 className="w-4 h-4 mr-2" /> {data.share_token ? "Copy Share Link" : "Share with Client"}
              </Button>
              <Button onClick={runAnalysis} disabled={analyzing} data-testid="analyze-btn"
                className="bg-gold hover:bg-gold/90 text-navy rounded-none font-semibold">
                {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Assessment</>}
              </Button>
            </div>
          )}
        </div>
        <div className="mt-6 flex items-center gap-3">
          <Progress value={progress} className="h-1 bg-navy/10 flex-1" />
          <span className="text-xs text-navy/60 tabular-nums">{progress}% complete</span>
          {saving && <span className="text-xs text-gold flex items-center"><Save className="w-3 h-3 mr-1" /> Saving...</span>}
        </div>
        <div className="mt-6 pt-6 border-t border-navy/10">
          <div className="eyebrow text-gold mb-2">Auto-Fill with Voice</div>
          <p className="text-xs text-navy/60 mb-3">
            Record yourself walking through the interview naturally — we'll transcribe and fill every section.
            You can still edit anything after.
          </p>
          <VoiceRecorder onUpload={voiceUpload} />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent gap-2 flex-wrap h-auto p-0 justify-start">
          {tabsToShow.map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
              className="rounded-none border border-navy/15 bg-white data-[state=active]:bg-navy data-[state=active]:text-ivory data-[state=active]:border-navy px-4 py-2">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="client" className="mt-6"><ClientTab data={data} update={update} /></TabsContent>
        <TabsContent value="context" className="mt-6"><ContextTab data={data} update={update} onVoice={voiceUpload} /></TabsContent>
        <TabsContent value="pains" className="mt-6"><PainsTab data={data} update={update} onVoice={voiceUpload} /></TabsContent>
        <TabsContent value="tools" className="mt-6"><ToolsTab data={data} update={update} onVoice={voiceUpload} /></TabsContent>
        <TabsContent value="goals" className="mt-6"><GoalsTab data={data} update={update} onVoice={voiceUpload} /></TabsContent>
        <TabsContent value="readiness" className="mt-6"><ReadinessTab data={data} update={update} /></TabsContent>
        <TabsContent value="finance" className="mt-6"><FinanceTab data={data} update={update} /></TabsContent>
        {showTranscriptTab && (
          <TabsContent value="transcript" className="mt-6">
            <TranscriptTab interviewId={id} data={data} setData={setData} />
          </TabsContent>
        )}
        {showTranscriptTab && (
          <TabsContent value="review" className="mt-6">
            <ReviewProfileTab interviewId={id} data={data} setData={setData} />
          </TabsContent>
        )}
      </Tabs>

      {showServiceRecommendation && (
        <ServiceRecommendationCard interviewId={id} data={data} setData={setData} />
      )}

      {!publicMode && data.assessment && (
        <div className="bg-navy text-ivory p-8 flex items-center justify-between">
          <div>
            <div className="eyebrow text-gold">Ready</div>
            <div className="font-display text-2xl mt-1">Assessment has been generated</div>
          </div>
          <Button onClick={() => nav(`/interviews/${id}/assessment`)} data-testid="view-assessment-btn"
            className="bg-gold hover:bg-gold/90 text-navy rounded-none font-semibold">
            View Assessment & Download PPT
          </Button>
        </div>
      )}

      {publicMode && (
        <div className="bg-navy text-ivory p-8 md:p-10">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div className="max-w-xl">
              <div className="eyebrow text-gold">Finished?</div>
              <div className="font-display text-2xl md:text-3xl mt-2">Submit your interview</div>
              <p className="text-ivory/70 text-sm mt-3 leading-relaxed">
                When you're done, hit submit — Goldline will get an instant notification with
                your answers and follow up with your recommendations. You can keep editing
                until you click submit.
              </p>
            </div>
            <Button
              onClick={submitPublic}
              disabled={submitting}
              data-testid="public-submit-btn"
              className="bg-gold hover:bg-gold/90 text-navy rounded-none font-semibold px-8 py-6 text-base"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-5 h-5 mr-2" /> Submit Interview</>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return publicMode ? <div className="min-h-screen bg-ivory grain">{content}</div> : <Layout>{content}</Layout>;
}

function computeProgress(d) {
  const fields = [
    d.client_name, d.business_name, d.industry,
    d.context?.business_summary, d.context?.team_description, d.context?.typical_day,
    d.tool_stack?.email, d.tool_stack?.project_management,
    d.goals?.primary_goal, d.goals?.budget_sensitivity,
    d.readiness?.preferred_approach,
    d.financial?.hourly_value,
  ];
  const filled = fields.filter((f) => f && String(f).trim() !== "" && f !== 0).length;
  const painFilled = (d.pain_points || []).filter((p) => p.description).length;
  return Math.min(100, Math.round(((filled + painFilled * 2) / (fields.length + 8)) * 100));
}
